import { useEffect, useState } from "react";
import { FONT } from "../lib/tokens";
import { buildAIContext } from "../lib/aiContext";
import { clampServings } from "../lib/recipeConstants";
import { useProfile } from "../lib/useProfile";
import { useIngredientInfo } from "../lib/useIngredientInfo";

// ── Side dishes — paired-course generation from a saved recipe ──────
//
// Renders on a main-course recipe page. Lists the sides already
// paired with this recipe (links ride in the main's recipe jsonb as
// `sideDishes: [{ slug, title }]`) and offers a "+ GENERATE A SIDE
// DISH" CTA. Generation is the existing background-stub pipeline:
// the CTA mints a pending stub in the book (WRITING pill) and fires
// runBackgroundRecipeGen with `prefs.pairWith` so the edge function's
// PAIRING hard-constraint block drafts a complement to THIS dish.
//
// The AI call is strictly OPT-IN (project rule): nothing here touches
// the network until the user taps GENERATE. The direction field is
// optional free text ("something green", "quick") that rides as the
// prompt's USER ASK.
//
// Sides resolve against the live book rows by slug — a deleted side
// leaves a dangling {slug,title} entry in the main's jsonb, which we
// hide silently (pruning would be a write for zero user value).

const PAPER_CARD = {
  marginTop: 14,
  padding: "14px 14px 12px",
  background: "rgba(255,250,236,0.6)",
  border: "1px solid rgba(124,94,44,0.22)",
  borderRadius: 10,
};

const KICKER = {
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
  color: "#9C7E40", letterSpacing: "0.14em",
};

// Resolve the main's sideDishes entries against the live book rows.
// Returns [{ entry, row, bookIndex }] for entries whose side still
// exists; dangling slugs are dropped.
function resolveSides(mainRow, allRecipes) {
  const entries = Array.isArray(mainRow?.recipe?.sideDishes)
    ? mainRow.recipe.sideDishes
    : [];
  return entries
    .map((entry) => {
      const bookIndex = allRecipes.findIndex((r) => r?.slug === entry?.slug);
      return bookIndex >= 0
        ? { entry, row: allRecipes[bookIndex], bookIndex }
        : null;
    })
    .filter(Boolean);
}

export default function RecipeSideDishSection({
  mainRow, allRecipes = [], pantry = [], userId, onJumpTo, onGenerateSide,
}) {
  const [open, setOpen] = useState(false);       // direction input revealed
  const [direction, setDirection] = useState("");
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  // Reset transient state on page turn so a half-typed direction
  // can't leak onto a different recipe (same pattern as RecipePage's
  // editing/makeMineOpen reset).
  useEffect(() => {
    setOpen(false); setDirection(""); setBusy(false); setErrMsg("");
  }, [mainRow.id]);

  // Viewer profile (dietary/allergies ride into the AI context) and
  // ingredient enrichment — both cached hooks, no per-render network.
  const { profile } = useProfile(userId);
  const ingredientInfo = useIngredientInfo();

  const main = mainRow.recipe || {};
  const sides = resolveSides(mainRow, allRecipes);

  const generate = async () => {
    if (busy || !onGenerateSide) return;
    setBusy(true);
    setErrMsg("");
    try {
      // Brand-safe pantry + profile context — same assembly AIRecipe
      // uses (brand never reaches the LLM; buildAIContext strips it).
      const built = buildAIContext({
        pantry,
        profile,
        ingredientInfo,
        cookLogs: [],
        mode: "rich",
        course: "side",
        priority: "classic",
      });
      const dir = direction.trim();
      // Match the main's serves when it has a sane value. Honored by
      // the edge fn via dishContract.intent.serves (FREEFORM tier is
      // verified pass-through in verifyContract).
      const servesNum = Math.round(Number(main.serves));
      const serves = Number.isFinite(servesNum) && servesNum > 0
        ? clampServings(servesNum)
        : null;
      const payload = {
        // Final-only calls never render the pantry block server-side
        // (buildFinalPrompt hard-codes skipPantry) — sending rows
        // would be pure request-body egress for zero prompt effect.
        // buildAIContext still runs above because `context` IS
        // rendered in final mode and carries the safety-critical
        // allergen exclusions + dietary profile.
        pantry: [],
        prefs: {
          course: "side",
          priority: "classic",
          mealPrompt: dir || "a side dish to complement the main course",
          // The anchor contract — the edge fn renders this as a HARD
          // CONSTRAINT (complement, don't duplicate protein/starch/
          // sauce). Name/amount only; the full recipe stays home.
          pairWith: {
            title: main.title || "the main course",
            course: main.course || "main",
            cuisine: main.cuisine || undefined,
            ingredients: (main.ingredients || [])
              .map((i) => ({ name: i?.item || i?.name, amount: i?.amount ?? null }))
              .filter((i) => typeof i.name === "string" && i.name.trim()),
          },
          ...(serves ? { dishContract: { tier: "FREEFORM", intent: { serves } } } : {}),
        },
        // Steer regenerations away from sides this main already has.
        avoidTitles: sides
          .map((s) => s.row.recipe?.title)
          .filter(Boolean),
        context: built.context,
        lockedIngredients: [],
        conceptFrame: null,
        libraryTitles: [],
      };
      const stubTitle = dir
        ? `Side: ${dir.slice(0, 60)}`
        : `A side for ${main.title || "this dish"}`;
      const pairedWith = { slug: mainRow.slug, title: main.title || "" };
      await onGenerateSide({
        mainRow,
        stubRecipe: {
          title: stubTitle,
          subtitle: "",
          emoji: "🥗",
          course: "side",
          ingredients: [],
          steps: [],
          _gen: "pending",
          _genStartedAt: new Date().toISOString(),
          pairedWith,
        },
        payload,
        finalizeInputs: { locked: [], priority: "classic", isDreamMode: false },
        stubMeta: { title: stubTitle, subtitle: "", emoji: "🥗", carry: { pairedWith } },
      });
      setOpen(false);
      setDirection("");
    } catch (e) {
      // TestFlight rule: failures surface inline, verbatim-ish.
      setErrMsg(String(e?.message || e || "Couldn't start the side dish."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={PAPER_CARD}>
      <div style={KICKER}>
        SIDE DISHES{sides.length > 0 ? ` · ${sides.length}` : ""}
      </div>

      {/* Paired sides — each row jumps to that side's book page.
          Pending/failed stubs get the same pill language as the TOC
          so the state reads identically everywhere. */}
      {sides.length > 0 && (
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column" }}>
          <style>{`@keyframes sdGenPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }`}</style>
          {sides.map(({ entry, row, bookIndex }) => {
            const gen = row.recipe?._gen || null;
            const title = row.recipe?.title || entry.title || "(untitled)";
            return (
              <div key={entry.slug}>
                <button
                  onClick={() => onJumpTo?.(bookIndex + 2)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center",
                    gap: 8, padding: "9px 2px",
                    background: "transparent", border: "none",
                    borderBottom: "1px dashed rgba(124,94,44,0.22)",
                    cursor: "pointer", textAlign: "left",
                  }}
                >
                  <span style={{
                    flex: 1, minWidth: 0,
                    fontFamily: FONT.itemTitle, fontSize: 16,
                    fontStyle: "italic", color: "#3A2A12",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {title}
                  </span>
                  {gen === "pending" && (
                    <span style={{
                      fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
                      background: "#C39023", color: "#FFFFFF",
                      letterSpacing: "0.08em", padding: "2px 7px", borderRadius: 4,
                      flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: "50%", background: "#FFFFFF",
                        animation: "sdGenPulse 1s ease-in-out infinite",
                      }} />
                      WRITING
                    </span>
                  )}
                  {gen === "failed" && (
                    <span style={{
                      fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
                      background: "#C0392B", color: "#FFFFFF",
                      letterSpacing: "0.08em", padding: "2px 7px", borderRadius: 4,
                      flexShrink: 0,
                    }}>
                      FAILED
                    </span>
                  )}
                  <span aria-hidden style={{
                    fontFamily: FONT.forma, fontSize: 14, color: "#9C7E40",
                    flexShrink: 0,
                  }}>
                    ›
                  </span>
                </button>
                {gen === "failed" && row.recipe?._genError && (
                  <div style={{
                    padding: "4px 2px 8px",
                    fontFamily: FONT.forma, fontSize: 12,
                    color: "#9A3B2C", lineHeight: 1.5,
                  }}>
                    {row.recipe._genError}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* CTA — reveals the direction field. No AI call until the
          explicit GENERATE tap. */}
      {!open ? (
        <button
          onClick={() => { setOpen(true); setErrMsg(""); }}
          style={{
            marginTop: 10, width: "100%", padding: "10px 12px",
            background: "transparent",
            border: "1px dashed rgba(47,143,131,0.55)",
            color: "#246A60", borderRadius: 8,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.10em", cursor: "pointer",
          }}
        >
          + GENERATE A SIDE DISH
        </button>
      ) : (
        <div style={{ marginTop: 10 }}>
          <input
            autoFocus
            value={direction}
            maxLength={80}
            enterKeyHint="go"
            onChange={(e) => setDirection(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); generate(); }
              else if (e.key === "Escape") { setOpen(false); setDirection(""); }
            }}
            placeholder={'Any direction? "something green" · "quick"'}
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "10px 12px",
              background: "rgba(255,252,241,0.9)",
              border: "1px solid rgba(124,94,44,0.30)",
              borderRadius: 8,
              fontFamily: FONT.forma, fontSize: 13, color: "#3A2A12",
              outline: "none",
            }}
          />
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <button
              onClick={generate}
              disabled={busy}
              style={{
                flex: 1, padding: "11px 12px",
                background: busy ? "rgba(47,143,131,0.45)" : "#2F8F83",
                border: "1px solid #26766B",
                color: "#FFFFFF", borderRadius: 8,
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                letterSpacing: "0.12em",
                cursor: busy ? "progress" : "pointer",
              }}
            >
              {busy ? "STARTING…" : "GENERATE →"}
            </button>
            <button
              onClick={() => { setOpen(false); setDirection(""); setErrMsg(""); }}
              aria-label="Cancel"
              style={{
                minWidth: 40, padding: "11px 12px",
                background: "transparent",
                border: "1px solid rgba(124,94,44,0.30)",
                color: "#7C5E2C", borderRadius: 8,
                fontFamily: FONT.forma, fontSize: 14, lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
          <div style={{
            marginTop: 6, fontFamily: FONT.forma, fontSize: 12,
            color: "#9C7E40", lineHeight: 1.5,
          }}>
            Drafts in the background — it lands in your book paired with this recipe.
          </div>
        </div>
      )}

      {errMsg && (
        <div style={{
          marginTop: 8, fontFamily: FONT.forma, fontSize: 12,
          color: "#9A3B2C", lineHeight: 1.5,
        }}>
          {errMsg}
        </div>
      )}
    </div>
  );
}

// ── "SIDE FOR …" attribution on the side's own page ─────────────────
//
// Mirrors the BASED ON provenance line. The main is derived by
// scanning the live rows for one whose sideDishes references this
// slug (rename-fresh, and survives the server-persist path that can
// drop the stub's own pairedWith stamp); the jsonb back-ref is the
// non-tappable fallback when the main is gone.
export function PairedWithAttribution({ row, allRecipes = [], onJumpTo }) {
  const slug = row?.slug;
  if (!slug) return null;
  const mainIndex = allRecipes.findIndex((r) =>
    Array.isArray(r?.recipe?.sideDishes) &&
    r.recipe.sideDishes.some((e) => e?.slug === slug),
  );
  const fallbackTitle = row?.recipe?.pairedWith?.title || null;
  if (mainIndex < 0 && !fallbackTitle) return null;
  const label = mainIndex >= 0
    ? (allRecipes[mainIndex].recipe?.title || "the main dish")
    : fallbackTitle;
  const line = (
    <span style={{
      fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
      color: "#7C5E2C", letterSpacing: "0.10em",
    }}>
      SIDE FOR {String(label).toUpperCase()}{mainIndex >= 0 ? " ›" : ""}
    </span>
  );
  if (mainIndex < 0) {
    return <div style={{ marginTop: 5 }}>{line}</div>;
  }
  return (
    <button
      onClick={() => onJumpTo?.(mainIndex + 2)}
      style={{
        // Padded to a ~30px tap target; bottom margin pulls the
        // following block back so the line reads flush like BASED ON.
        display: "block", margin: "5px 0 -8px",
        padding: "4px 8px 8px 0",
        background: "transparent", border: "none",
        cursor: "pointer", textAlign: "left",
      }}
    >
      {line}
    </button>
  );
}
